import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  useTheme,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  Folder as FolderIcon,
  RateReview as ReviewIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { projectService, SubmitProjectData, RiskScoreResponse } from '../services/projectService';
import { userService } from '../services/userService';
import SuccessModal from '../components/SuccessModal';
import { useThemeMode } from '../contexts/ThemeContext';

const steps = [
  { label: 'Asset Information', icon: DescriptionIcon, description: 'Basic details about your asset' },
  { label: 'Valuation & Token', icon: MoneyIcon, description: 'Set value and token parameters' },
  { label: 'Risk Assessment', icon: AssessmentIcon, description: 'AI-powered risk analysis' },
  { label: 'Upload Documents', icon: FolderIcon, description: 'Supporting documentation' },
  { label: 'Review & Submit', icon: ReviewIcon, description: 'Final review before submission' },
];

const projectTypes = [
  { value: 'real_estate', label: 'Real Estate', icon: '🏢' },
  { value: 'art', label: 'Art & Collectibles', icon: '🎨' },
  { value: 'invoice', label: 'Invoice Financing', icon: '📄' },
  { value: 'equipment', label: 'Equipment', icon: '⚙️' },
  { value: 'agriculture', label: 'Agriculture', icon: '🌾' },
];

const SubmitProject = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState<File[]>([]);
  const [riskScore, setRiskScore] = useState<RiskScoreResponse | null>(null);
  const [calculatingRisk, setCalculatingRisk] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedProjectData, setSubmittedProjectData] = useState<any>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    trigger,
  } = useForm<SubmitProjectData>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      project_type: 'real_estate',
      valuation_usd: 1000,
      tokenized_percent: 100,
      user_id: '', // This will be set from wallet
      location: '',
      token_name: '',
      token_symbol: '',
    },
  });

  const watchedValues = watch();

  // Check wallet connection
  useEffect(() => {
    if (!isConnected) {
      setError('Please connect your wallet to submit a project');
    } else {
      setError(null);
    }
  }, [isConnected]);

  const handleNext = async () => {
    if (activeStep === 0 && !isConnected) {
      setError('Please connect your wallet first');
      return;
    }
    // Don't proceed to next step if we're on the last step
    if (activeStep === steps.length - 1) {
      return;
    }

    // Validate current step before proceeding
    let isValid = false;
    
    if (activeStep === 0) {
      isValid = await trigger(['title', 'description', 'project_type', 'location'] as const);
    } else if (activeStep === 1) {
      isValid = await trigger(['valuation_usd', 'tokenized_percent', 'token_name', 'token_symbol'] as const);
    } else {
      isValid = true; // For other steps, just proceed
    }

    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields correctly before proceeding');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/dashboard/issuer');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setDocuments((prev) => [...prev, ...files]);
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateRiskScore = async () => {
    if (!watchedValues.valuation_usd || !watchedValues.project_type) return;

    setCalculatingRisk(true);
    try {
      const riskData = await projectService.calculateRiskScore(
        'temp-project-id', // This would be the actual project ID
        watchedValues.valuation_usd,
        watchedValues.project_type
      );
      setRiskScore(riskData);
    } catch (error) {
      console.error('Risk calculation failed:', error);
      setError('Failed to calculate risk score. Please try again.');
    } finally {
      setCalculatingRisk(false);
    }
  };

  const onSubmit = async (data: SubmitProjectData) => {
    console.log('Form submission triggered, activeStep:', activeStep);
    
    if (!isConnected || !address) {
      setError('Please connect your wallet to submit a project');
      return;
    }

    // Only submit if we're on the last step
    if (activeStep !== steps.length - 1) {
      console.log('Not on last step, preventing submission');
      setError('Please complete all steps before submitting');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate all fields before submission
      const isValid = await trigger();
      console.log('Form validation result:', isValid);
      console.log('Form data:', data);
      if (!isValid) {
        setError('Please fill in all required fields correctly');
        setLoading(false);
        return;
      }

      // Additional validation checks with better error messages
      if (!data.title || data.title.length < 10) {
        setError('Title must be at least 10 characters long');
        setLoading(false);
        return;
      }
      if (!data.description || data.description.length < 10) {
        setError('Description must be at least 10 characters long');
        setLoading(false);
        return;
      }
      if (!data.location || data.location.length < 1) {
        setError('Location is required');
        setLoading(false);
        return;
      }
      if (!data.token_name || data.token_name.length < 1) {
        setError('Token name is required');
        setLoading(false);
        return;
      }
      if (!data.token_symbol || data.token_symbol.length < 1) {
        setError('Token symbol is required');
        setLoading(false);
        return;
      }

      // Get or create user first with better error handling
      let user;
      try {
        user = await userService.getUserByWallet(address);
        console.log('User found:', user);
      } catch (error: any) {
        console.log('User not found, creating new user...');
        // If user doesn't exist, create one
        try {
          user = await userService.createUser({
            wallet_address: address,
            role: 'issuer'
          });
          console.log('User created:', user);
        } catch (createError: any) {
          console.error('Failed to create user:', createError);
          if (createError?.response?.data?.message) {
            setError(`Failed to create user account: ${createError.response.data.message}`);
          } else {
            setError('Failed to create user account. Please try again.');
          }
          setLoading(false);
          return;
        }
      }

      // Create project data with user UUID
      const projectData = {
        ...data,
        user_id: user.id, // Use the user's UUID instead of wallet address
      };

      console.log('Submitting project data:', projectData);

      const response = await projectService.submitProject(projectData);
      console.log('Project submission response:', response);
      setSubmittedProjectData(data);
      setShowSuccessModal(true);
      setSuccess(true);
    } catch (error: any) {
      console.error('Project submission failed:', error);
      if (error?.response?.data?.detail) {
        const validationErrors = error.response.data.detail;
        const errorMessages = validationErrors.map((err: any) => err.msg).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else if (error?.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error?.message) {
        setError(`Submission failed: ${error.message}`);
      } else {
        setError('Failed to submit project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
                             <Grid item xs={12}>
                 <Controller
                   name="title"
                   control={control}
                   rules={{ 
                     required: 'Project title is required',
                     minLength: { value: 10, message: 'Title must be at least 10 characters long' }
                   }}
                   render={({ field }) => (
                     <TextField
                       {...field}
                       fullWidth
                       label="Project Title"
                       placeholder="Enter a descriptive title for your project (minimum 10 characters)"
                       error={!!errors.title}
                       helperText={errors.title?.message || `${field.value?.length || 0}/10 characters minimum`}
                       inputProps={{ maxLength: 100 }}
                     />
                   )}
                 />
               </Grid>
                             <Grid item xs={12}>
                 <Controller
                   name="description"
                   control={control}
                   rules={{ 
                     required: 'Description is required',
                     minLength: { value: 10, message: 'Description must be at least 10 characters long' }
                   }}
                   render={({ field }) => (
                     <TextField
                       {...field}
                       fullWidth
                       multiline
                       rows={4}
                       label="Project Description"
                       placeholder="Describe your project in detail (minimum 10 characters)"
                       error={!!errors.description}
                       helperText={errors.description?.message || `${field.value?.length || 0}/10 characters minimum`}
                       inputProps={{ maxLength: 500 }}
                     />
                   )}
                 />
               </Grid>
              <Grid item xs={12}>
                <Controller
                  name="project_type"
                  control={control}
                  rules={{ required: 'Project type is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.project_type}>
                      <InputLabel>Project Type</InputLabel>
                      <Select {...field} label="Project Type">
                        {projectTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="location"
                  control={control}
                  rules={{ 
                    required: 'Location is required',
                    minLength: { value: 1, message: 'Location must be at least 1 character long' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Asset Location"
                      error={!!errors.location}
                      helperText={errors.location?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="valuation_usd"
                  control={control}
                  rules={{ 
                    required: 'Valuation is required',
                    min: { value: 1000, message: 'Minimum valuation is $1,000' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Asset Valuation (USD)"
                      error={!!errors.valuation_usd}
                      helperText={errors.valuation_usd?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="tokenized_percent"
                  control={control}
                  rules={{ 
                    required: 'Tokenized percentage is required',
                    min: { value: 1, message: 'Minimum 1%' },
                    max: { value: 100, message: 'Maximum 100%' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Tokenized Percentage (%)"
                      error={!!errors.tokenized_percent}
                      helperText={errors.tokenized_percent?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="token_name"
                  control={control}
                  rules={{ 
                    required: 'Token name is required',
                    minLength: { value: 1, message: 'Token name must be at least 1 character long' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Token Name"
                      error={!!errors.token_name}
                      helperText={errors.token_name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="token_symbol"
                  control={control}
                  rules={{ 
                    required: 'Token symbol is required',
                    minLength: { value: 1, message: 'Token symbol must be at least 1 character long' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Token Symbol"
                      error={!!errors.token_symbol}
                      helperText={errors.token_symbol?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Risk Assessment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Our AI will analyze your asset and provide a comprehensive risk score
              </Typography>
              
              {!riskScore && (
                <Button
                  variant="contained"
                  onClick={calculateRiskScore}
                  disabled={calculatingRisk || !watchedValues.valuation_usd}
                  startIcon={calculatingRisk ? <CircularProgress size={20} /> : <TrendingUpIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                    },
                  }}
                >
                  {calculatingRisk ? 'Calculating...' : 'Calculate Risk Score'}
                </Button>
              )}
            </Box>

            {riskScore && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Risk Assessment Results
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary.main" fontWeight={700}>
                          {riskScore.score}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Overall Risk Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="info.main" fontWeight={600}>
                          {riskScore.liquidity_score}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Liquidity Risk
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="warning.main" fontWeight={600}>
                          {riskScore.credit_score}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Credit Risk
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="error.main" fontWeight={600}>
                          {riskScore.volatility_score}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Volatility Risk
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Risk assessment completed using AI-powered analysis. 
                      This score will help investors make informed decisions.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Upload Area */}
            <Box
              sx={{
                p: 4,
                borderRadius: 2,
                border: `2px dashed ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                background: isDark ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.03)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#3B82F6',
                  background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.06)',
                },
              }}
            >
              <input
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="document-upload"
                multiple
                type="file"
                onChange={handleFileUpload}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: '#3B82F6', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
                Drag & drop files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                or click to browse
              </Typography>
              <label htmlFor="document-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                    },
                  }}
                >
                  Choose Files
                </Button>
              </label>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                Supported: PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
              </Typography>
            </Box>

            {/* Uploaded Files List */}
            {documents.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                  Uploaded Documents ({documents.length})
                </Typography>
                {documents.map((file, index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FolderIcon sx={{ color: '#3B82F6' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => removeDocument(index)}
                      startIcon={<DeleteIcon />}
                      sx={{
                        color: '#EF4444',
                        '&:hover': {
                          background: 'rgba(239, 68, 68, 0.1)',
                        },
                      }}
                    >
                      Remove
                    </Button>
                  </Paper>
                ))}
              </Box>
            )}
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h6" gutterBottom>
              Review Your Project
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Project Title
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {watchedValues.title}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Project Type
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {projectTypes.find(t => t.value === watchedValues.project_type)?.label}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {watchedValues.description}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Valuation
                </Typography>
                <Typography variant="body1" gutterBottom>
                  ${watchedValues.valuation_usd?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Tokenized Percentage
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {watchedValues.tokenized_percent}%
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Token Symbol
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {watchedValues.token_symbol || 'Not specified'}
                </Typography>
              </Grid>
              {riskScore && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Risk Score
                  </Typography>
                  <Chip
                    label={`${riskScore.score}/100`}
                    color={riskScore.score < 30 ? 'success' : riskScore.score < 60 ? 'warning' : 'error'}
                    sx={{ mr: 1 }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Documents ({documents.length})
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {documents.map((file) => file.name).join(', ') || 'No documents'}
                </Typography>
              </Grid>
            </Grid>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const renderResponsiveStepper = () => {
    if (isMobile) {
      // Mobile view: Show step icons with current step highlighted
      return (
        <Box sx={{ mb: 4 }}>
          {/* Step Icons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              return (
                <Box
                  key={step.label}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isActive
                      ? 'linear-gradient(135deg, #3B82F6, #0EA5E9)'
                      : isCompleted
                        ? 'rgba(16, 185, 129, 0.15)'
                        : isDark
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.05)',
                    border: `2px solid ${
                      isActive
                        ? 'transparent'
                        : isCompleted
                          ? 'rgba(16, 185, 129, 0.3)'
                          : isDark
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)'
                    }`,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 18, color: '#10B981' }} />
                  ) : (
                    <StepIcon
                      sx={{
                        fontSize: 18,
                        color: isActive ? '#fff' : 'text.secondary',
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    } else {
      // Desktop view: Show custom stepper with icons
      return (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
            {/* Connection line */}
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: 40,
                right: 40,
                height: 2,
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: 40,
                height: 2,
                bgcolor: '#3B82F6',
                width: `${(activeStep / (steps.length - 1)) * (100 - 10)}%`,
                transition: 'width 0.4s ease',
                zIndex: 1,
              }}
            />

            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              return (
                <Box
                  key={step.label}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: 2,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isActive
                        ? 'linear-gradient(135deg, #3B82F6, #0EA5E9)'
                        : isCompleted
                          ? 'rgba(16, 185, 129, 0.15)'
                          : isDark
                            ? 'rgba(17, 24, 39, 1)'
                            : 'rgba(255, 255, 255, 1)',
                      border: `2px solid ${
                        isActive
                          ? 'transparent'
                          : isCompleted
                            ? 'rgba(16, 185, 129, 0.4)'
                            : isDark
                              ? 'rgba(255, 255, 255, 0.15)'
                              : 'rgba(0, 0, 0, 0.12)'
                      }`,
                      boxShadow: isActive
                        ? '0 4px 15px rgba(59, 130, 246, 0.4)'
                        : 'none',
                      transition: 'all 0.3s ease',
                      mb: 1,
                    }}
                  >
                    {isCompleted ? (
                      <CheckIcon sx={{ fontSize: 20, color: '#10B981' }} />
                    ) : (
                      <StepIcon
                        sx={{
                          fontSize: 20,
                          color: isActive ? '#fff' : 'text.secondary',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isActive ? '#3B82F6' : isCompleted ? '#10B981' : 'text.secondary',
                      fontWeight: isActive ? 600 : 400,
                      textAlign: 'center',
                      maxWidth: 80,
                      lineHeight: 1.2,
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="md">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Submit Your Project
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Tokenize your real-world assets with institutional-grade compliance
            </Typography>
          </Box>
        </motion.div>

        {/* Alerts */}
        {!isConnected && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              background: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
              border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)'}`,
            }}
          >
            Please connect your wallet to submit a project
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              background: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
              border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              background: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
              border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
            }}
          >
            Project submitted successfully! Redirecting to dashboard...
          </Alert>
        )}

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card
            sx={{
              mb: 4,
              background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {renderResponsiveStepper()}

              {/* Current Step Info */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                  border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {React.createElement(steps[activeStep].icon, {
                    sx: { color: '#3B82F6', fontSize: 28 }
                  })}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {steps[activeStep].label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {steps[activeStep].description}
                    </Typography>
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 6,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #3B82F6, #0EA5E9)',
                        width: `${((activeStep + 1) / steps.length) * 100}%`,
                        transition: 'width 0.4s ease',
                        borderRadius: 3,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#3B82F6',
                      fontWeight: 600,
                      minWidth: 45,
                    }}
                  >
                    {Math.round(((activeStep + 1) / steps.length) * 100)}%
                  </Typography>
                </Box>
              </Box>

              <form onSubmit={handleSubmit(onSubmit)}>
                {renderStepContent(activeStep)}

                {/* Navigation Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 5,
                    pt: 3,
                    borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  }}
                >
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    type="button"
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        color: '#3B82F6',
                        background: 'rgba(59, 130, 246, 0.08)',
                      },
                      '&:disabled': {
                        opacity: 0.4,
                      },
                    }}
                  >
                    Back
                  </Button>

                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={loading || !isValid || !isConnected}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                          boxShadow: '0 4px 15px rgba(30, 64, 175, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                            boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)',
                          },
                        }}
                      >
                        {loading ? 'Submitting...' : 'Submit Project'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isConnected}
                        endIcon={<ArrowForwardIcon />}
                        type="button"
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                          boxShadow: '0 4px 15px rgba(30, 64, 175, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
                            boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)',
                          },
                        }}
                      >
                        Continue
                      </Button>
                    )}
                  </Box>
                </Box>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Success Modal */}
        {submittedProjectData && (
          <SuccessModal
            open={showSuccessModal}
            onClose={handleSuccessModalClose}
            projectData={submittedProjectData}
          />
        )}
      </Container>
    </Box>
  );
};

export default SubmitProject; 